import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@mui/styles';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  TablePagination,
  TextField,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from "react-i18next";
import { colors } from '../components/Theme/WebTheme';
import RolesAPI from '../common/rolesApi';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: '20px'
  },
  headerSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px'
  },
  searchBox: {
    marginBottom: '20px',
    maxWidth: '400px'
  },
  tableContainer: {
    marginTop: '20px',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
  },
  tableHeader: {
    backgroundColor: colors.MAIN_COLOR,
    '& .MuiTableCell-head': {
      color: 'white',
      fontWeight: 'bold'
    }
  },
  activityRow: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#f5f5f5'
    },
    '&:hover': {
      backgroundColor: '#e3f2fd'
    }
  },
  actionChip: {
    fontWeight: 'bold',
    color: 'white'
  },
  createActionChip: {
    backgroundColor: '#4CAF50'
  },
  updateActionChip: {
    backgroundColor: '#2196F3'
  },
  deleteActionChip: {
    backgroundColor: '#f44336'
  },
  assignActionChip: {
    backgroundColor: '#FF9800'
  },
  removeActionChip: {
    backgroundColor: '#9C27B0'
  },
  timestampCell: {
    fontSize: '0.875rem',
    color: '#666'
  },
  detailsCell: {
    maxWidth: '300px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: '#666'
  }
}));

export default function ActivityLogsTab() {
  const classes = useStyles();
  const { t } = useTranslation();

  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredActivities, setFilteredActivities] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const loadActivities = useCallback(async () => {
    try {
      setLoading(true);
      const response = await RolesAPI.getRoleActivities(100, 0); // Load more activities for better filtering

      if (response.success) {
        setActivities(response.activities || []);
      } else {
        showSnackbar(t('error_loading_activities'), 'error');
      }
    } catch (error) {
      console.error('Error loading activities:', error);
      showSnackbar(t('error_loading_activities'), 'error');
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    loadActivities();
  }, [loadActivities]);

  useEffect(() => {
    // Filter activities based on search term
    const filtered = activities.filter(activity =>
      activity.userName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.action?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      activity.details?.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredActivities(filtered);
    setPage(0); // Reset pagination when filtering
  }, [activities, searchTerm]);

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleRefresh = () => {
    loadActivities();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getActionChipClass = (action) => {
    if (action.includes('created')) return classes.createActionChip;
    if (action.includes('updated')) return classes.updateActionChip;
    if (action.includes('deleted')) return classes.deleteActionChip;
    if (action.includes('assigned')) return classes.assignActionChip;
    if (action.includes('removed')) return classes.removeActionChip;
    return classes.updateActionChip;
  };

  const renderActionChip = (action) => (
    <Chip
      label={t(action)}
      className={`${classes.actionChip} ${getActionChipClass(action)}`}
      size="small"
    />
  );

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '-';
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  };

  const paginatedActivities = filteredActivities.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <div className={classes.container}>
      <div className={classes.headerSection}>
        <div>
          <Typography variant="h5" style={{ fontWeight: 'bold', marginBottom: '8px' }}>
            {t('activity_logs')}
          </Typography>
          <Typography variant="body1" style={{ color: '#666' }}>
            {t('view_all_role_activities')}
          </Typography>
        </div>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleRefresh}
          disabled={loading}
        >
          {t('refresh')}
        </Button>
      </div>

      <TextField
        className={classes.searchBox}
        placeholder={t('search_activities')}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        variant="outlined"
        size="small"
      />

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table>
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <TableCell>{t('date_time')}</TableCell>
              <TableCell>{t('user')}</TableCell>
              <TableCell>{t('action')}</TableCell>
              <TableCell>{t('details')}</TableCell>
              <TableCell>{t('page')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedActivities.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className={classes.emptyState}>
                  {searchTerm ? t('no_activities_found') : t('no_activities_yet')}
                </TableCell>
              </TableRow>
            ) : (
              paginatedActivities.map((activity) => (
                <TableRow key={activity.id} className={classes.activityRow}>
                  <TableCell className={classes.timestampCell}>
                    <div>
                      <div>{formatTimestamp(activity.timestamp).split(' ')[0]}</div>
                      <div style={{ fontSize: '0.75rem', color: '#999' }}>
                        {formatTime(activity.timestamp)}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div>
                      <div style={{ fontWeight: 'bold' }}>
                        {activity.userName || t('unknown_user')}
                      </div>
                      <div style={{ fontSize: '0.875rem', color: '#666' }}>
                        {activity.userEmail}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    {renderActionChip(activity.action)}
                  </TableCell>
                  <TableCell className={classes.detailsCell} title={activity.details}>
                    {activity.details || '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={activity.page || t('system')}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100]}
        component="div"
        count={filteredActivities.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage={t('rows_per_page')}
        labelDisplayedRows={({ from, to, count }) =>
          `${from}-${to} ${t('of')} ${count !== -1 ? count : `${t('more_than')} ${to}`}`
        }
      />

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
} 